import { AddCircleOutlined, InfoOutlined } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import TableRowsOutlined from "@mui/icons-material/TableRowsOutlined";
import { Alert, Box, Tooltip } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../../components/UIToolkit/Animations/JumpingDots";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import type { MultiSortOption } from "../../../../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { useDebouncedSave } from "../../../../../hooks/useDebouncedSave";
import { useErrorSnackbar } from "../../../../../hooks/useErrorSnackbar";
import { hrisMappingEmploymentSortingKey } from "../../../../../services/LocalStorageService";
import { EmploymentType, type EmploymentTypeMapping, HrisIgnoreCategory } from "../../../../../utils/models";
import {
	useCreateHrisIgnoredValue,
	useDeleteEmploymentMapping,
	useDeleteHrisIgnoredValue,
	useDepartmentEmploymentTypeMapping,
	useHrisIgnoredValues,
	useHrisRawEmployees,
	useUpsertEmploymentMapping,
} from "./hrisMappingHooks";
import { type AddCustomFieldSpec, AddCustomMappingButton, HoverActionCell, IgnoredCell } from "./utils";

export const EmploymentTypeMappingGrid: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const departmentId = chosenDepartment.department_id;
	const { data, isLoading, isError } = useDepartmentEmploymentTypeMapping(departmentId);
	const { data: rawEmployees, isLoading: isLoadingRaw } = useHrisRawEmployees(departmentId);
	const upsertMutation = useUpsertEmploymentMapping(departmentId);
	const deleteMutation = useDeleteEmploymentMapping(departmentId);
	const { data: ignoredRows } = useHrisIgnoredValues(departmentId);
	const createIgnoredMutation = useCreateHrisIgnoredValue(departmentId);
	const deleteIgnoredMutation = useDeleteHrisIgnoredValue(departmentId);
	const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
	// Shared open state so the toolbar button and the in-grid EmptyState action drive the same dialog.
	const [addDialogOpen, setAddDialogOpen] = useState(false);

	// Map raw_value (lowercased) → ignore row, scoped to the employment_type category. Global
	// rows (`departmentId === null`) and per-dept rows are both surfaced — admins can ignore
	// per-dept from the grid; global rules show up as already-ignored.
	const employmentIgnoreByRaw = useMemo(() => {
		const m = new Map<string, { id: number; isGlobal: boolean }>();
		for (const row of ignoredRows ?? []) {
			if (row.category !== HrisIgnoreCategory.EmploymentType) continue;
			m.set(row.rawValue.toLowerCase(), { id: row.id, isGlobal: row.departmentId === null });
		}
		return m;
	}, [ignoredRows]);

	// Lowercased set of employment_type strings present in raw employees for this department.
	// Used to filter the grid down to types actually arriving in HRIS data.
	const rawEmploymentTypeSet = useMemo(() => {
		const s = new Set<string>();
		for (const e of rawEmployees ?? []) {
			const v = e.employmentType.trim().toLowerCase();
			if (v) s.add(v);
		}
		return s;
	}, [rawEmployees]);

	const orphanCount = useMemo(() => {
		if (!data) return 0;
		const mappingKeys = new Set(data.map((m) => m.rawString.toLowerCase()));
		let count = 0;
		for (const v of rawEmploymentTypeSet) if (!mappingKeys.has(v)) count++;
		return count;
	}, [data, rawEmploymentTypeSet]);

	const empSortOptions: MultiSortOption<EmploymentTypeMapping>[] = useMemo(
		() => [
			{ label: t("hris.mapping.column.rawString"), sortColumns: ["rawString"] },
			{ label: t("hris.mapping.column.employmentType"), sortColumns: ["employmentType"] },
			{ label: t("hris.mapping.column.isOverride"), sortColumns: ["isOverride"] },
			{ label: t("hris.mapping.column.isCustom"), sortColumns: ["isCustom"] },
		],
		[t],
	);

	const scheduleSave = useDebouncedSave<{ rawString: string; employmentType: string }>((payload) => {
		upsertMutation.mutate(payload, {
			onError: (err) => showErrorSnackbar(t("hris.mapping.saveError"), {}, err),
			onSettled: () =>
				setPendingKeys((prev) => {
					const next = new Set(prev);
					next.delete(payload.rawString);
					return next;
				}),
		});
	});

	const handleDelete = useCallback(
		(rawString: string) => {
			deleteMutation.mutate(rawString, { onError: (err) => showErrorSnackbar(t("hris.mapping.deleteError"), {}, err) });
		},
		[deleteMutation, showErrorSnackbar, t],
	);

	const handleToggleIgnore = useCallback(
		(rawString: string) => {
			const existing = employmentIgnoreByRaw.get(rawString.toLowerCase());
			if (existing) {
				// Global rules are intentionally not removable from a single department's grid —
				// they require a follow-up SQL action by ops. Only per-dept rows can be removed here.
				if (existing.isGlobal) return;
				deleteIgnoredMutation.mutate(existing.id, { onError: (err) => showErrorSnackbar(t("hris.mapping.deleteError"), {}, err) });
				return;
			}
			createIgnoredMutation.mutate(
				{ category: HrisIgnoreCategory.EmploymentType, rawValue: rawString, departmentId },
				{ onError: (err) => showErrorSnackbar(t("hris.mapping.saveError"), {}, err) },
			);
		},
		[createIgnoredMutation, deleteIgnoredMutation, departmentId, employmentIgnoreByRaw, showErrorSnackbar, t],
	);

	const empAddFields: AddCustomFieldSpec[] = useMemo(
		() => [
			{ name: "employmentType", label: t("hris.mapping.column.employmentType"), options: Object.values(EmploymentType).map((v) => ({ value: v, label: v })) },
		],
		[t],
	);

	const handleAddCustom = useCallback(
		(key: string, extras: Record<string, string>) => {
			upsertMutation.mutate(
				{ rawString: key, employmentType: extras.employmentType },
				{ onError: (err) => showErrorSnackbar(t("hris.mapping.saveError"), {}, err) },
			);
		},
		[upsertMutation, showErrorSnackbar, t],
	);

	const processRowUpdate = useCallback(
		(newRow: EmploymentTypeMapping, oldRow: EmploymentTypeMapping): EmploymentTypeMapping => {
			if (newRow.employmentType !== oldRow.employmentType) {
				setPendingKeys((prev) => new Set(prev).add(newRow.rawString));
				scheduleSave(newRow.rawString, { rawString: newRow.rawString, employmentType: newRow.employmentType });
			}
			return newRow;
		},
		[scheduleSave],
	);

	// Custom rows are ALWAYS visible; other rows must appear in raw employees for this department.
	// `isCustom` rows render with `isOverride: false` to suppress the restore icon.
	const rows = useMemo(
		() =>
			(data ?? []).filter((r) => r.isCustom || rawEmploymentTypeSet.has(r.rawString.toLowerCase())).map((r) => (r.isCustom ? { ...r, isOverride: false } : r)),
		[data, rawEmploymentTypeSet],
	);

	const columns: GridColDef<EmploymentTypeMapping>[] = useMemo(
		() => [
			{
				field: "rawString",
				headerName: t("hris.mapping.column.rawString"),
				flex: 1,
				minWidth: 200,
			},
			{
				field: "employmentType",
				headerName: t("hris.mapping.column.employmentType"),
				flex: 1,
				minWidth: 160,
				editable: true,
				type: "singleSelect",
				valueOptions: Object.values(EmploymentType),
			},
			{
				field: "isOverride",
				headerName: t("hris.mapping.column.isOverride"),
				width: 100,
				align: "center",
				headerAlign: "center",
				display: "flex",
				renderCell: (params) =>
					pendingKeys.has(params.row.rawString) ? (
						<JumpingDots />
					) : (
						<HoverActionCell
							show={params.row.isOverride && !params.row.isCustom}
							actionIcon={<RestoreIcon fontSize="small" />}
							tooltip={params.row.defaultDisplay ? `${t("hris.mapping.resetToDefault")}: ${params.row.defaultDisplay}` : t("hris.mapping.resetToDefault")}
							onAction={() => handleDelete(params.row.rawString)}
						/>
					),
			},
			{
				field: "isCustom",
				headerName: t("hris.mapping.column.isCustom"),
				width: 100,
				align: "center",
				headerAlign: "center",
				display: "flex",
				renderCell: (params) => (
					<HoverActionCell
						show={params.row.isCustom}
						actionIcon={<DeleteOutlineIcon fontSize="small" color="error" />}
						tooltip={t("hris.mapping.deleteCustom")}
						onAction={() => handleDelete(params.row.rawString)}
					/>
				),
			},
			{
				field: "ignored",
				headerName: t("hris.mapping.column.ignored"),
				width: 100,
				align: "center",
				headerAlign: "center",
				display: "flex",
				sortable: false,
				renderCell: (params) => (
					<IgnoredCell
						ignore={employmentIgnoreByRaw.get(params.row.rawString.toLowerCase())}
						rawValue={params.row.rawString}
						onToggle={() => handleToggleIgnore(params.row.rawString)}
					/>
				),
			},
		],
		[t, handleDelete, handleToggleIgnore, employmentIgnoreByRaw, pendingKeys],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, minHeight: 0 }}>
			{orphanCount > 0 && (
				<Alert severity="info" data-testid="hris-mapping-employment-orphan-banner" sx={{ alignItems: "center" }}>
					{t("hris.mapping.orphans.banner.employment", { count: orphanCount })}
				</Alert>
			)}
			<SystemDataGrid<EmploymentTypeMapping>
				rows={rows}
				columns={columns}
				getRowId={(row) => row.rawString}
				searchableColumns={["rawString"]}
				searchPlaceholder={t("hris.mapping.search.placeholder.rawString")}
				sortingOptions={empSortOptions}
				defaultSortState={[{ sortColumns: ["rawString"], sortOrder: "asc" }]}
				localStorageKey={hrisMappingEmploymentSortingKey}
				processRowUpdate={processRowUpdate}
				loading={isLoading || isLoadingRaw}
				error={isError}
				containedHeight
				emptyState={
					<EmptyState
						icon={<TableRowsOutlined />}
						title={t("hris.mapping.grid.empty.title")}
						hint={t("hris.mapping.grid.empty.hint")}
						action={
							<SystemButton
								buttonVariant="secondary"
								size="small"
								leadingIcon={<AddCircleOutlined fontSize="small" />}
								onClick={() => setAddDialogOpen(true)}
								data-testid="hris-mapping-employment-empty-add"
							>
								{t("hris.mapping.addCustom")}
							</SystemButton>
						}
					/>
				}
				leadingToolbarNodes={
					<AddCustomMappingButton
						onAdd={handleAddCustom}
						keyLabel={t("hris.mapping.column.rawString")}
						extraFields={empAddFields}
						open={addDialogOpen}
						onOpenChange={setAddDialogOpen}
						hideTriggerButton={false}
					/>
				}
				trailingToolbarNodes={
					<Tooltip title={t("hris.mapping.addCustom.tooltip")}>
						<InfoOutlined />
					</Tooltip>
				}
				dataTestId="hris-mapping-employment"
			/>
		</Box>
	);
};
